import { useEffect, useState } from "react";
import { QrCode, Copy, Download, Check, X, UserPlus, Trash2, Eraser, Printer } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fecha } from "@/lib/format";
import { Loading, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_school-hub-1220/artifacts/eggcq1ix_302319671_479406710859175_2268986965834108785_n.jpg";

const TEXTOS = {
  tutor: {
    tituloQr: "Código QR para padres y tutores",
    archivo: "qr-registro-padres.png",
    portal: "Portal de padres y tutores",
    tituloHoja: "Regístrese para ver todo el colegio de su hijo",
    pasos: [
      "Escanee este código con la cámara de su teléfono.",
      "Complete el formulario con sus datos y los de su hijo o hija.",
      "La administración revisará su solicitud y le avisará por correo cuando quede aprobada.",
      "Luego podrá entrar al portal para ver calificaciones, tareas y su estado de cuenta.",
    ],
  },
  docente: {
    tituloQr: "Código QR para docentes",
    archivo: "qr-registro-docentes.png",
    portal: "Portal de docentes",
    tituloHoja: "Regístrese en el portal de docentes",
    pasos: [
      "Escanee este código con la cámara de su teléfono.",
      "Complete sus datos, su título y las materias o grados que imparte.",
      "La coordinación revisará su solicitud y le avisará por correo cuando quede aprobada.",
      "Luego podrá publicar calificaciones, marcar asistencia, subir tareas y enviar su planificación.",
    ],
  },
};

export const PanelRegistroQR = ({ rol = "tutor" }) => {
  const { config } = useAuth();
  const t = TEXTOS[rol];
  const [qr, setQr] = useState(null);
  const [enlace, setEnlace] = useState("");
  const [solicitudes, setSolicitudes] = useState(null);

  const cargar = () => {
    api.get("/solicitudes-registro", { params: { rol } })
      .then((r) => setSolicitudes(r.data)).catch(() => setSolicitudes([]));
  };

  useEffect(() => {
    setSolicitudes(null);
    setQr(null);
    api.get("/registro/enlace", { params: { rol } }).then((r) => setEnlace(r.data.url));
    api.get("/registro/qr", { params: { rol }, responseType: "blob" })
      .then((r) => setQr(URL.createObjectURL(r.data)));
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rol]);

  const decidir = async (s, accion) => {
    try {
      const { data } = await api.post(`/solicitudes-registro/${s.id}/${accion}`);
      const base = accion === "aprobar" ? `${s.nombre} ya puede iniciar sesión.` : "Solicitud rechazada.";
      if (data?.correo_enviado) toast.success(`${base} Le avisamos a ${s.email} por correo.`);
      else toast.warning(`${base} No pudimos enviarle el correo de aviso a ${s.email}.`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const imprimirHoja = () => {
    document.body.classList.add("imprimir-hoja");
    const limpiar = () => {
      document.body.classList.remove("imprimir-hoja");
      window.removeEventListener("afterprint", limpiar);
    };
    window.addEventListener("afterprint", limpiar);
    window.print();
  };

  const eliminar = async (s) => {
    try {
      await api.delete(`/solicitudes-registro/${s.id}`);
      toast.success(`Solicitud de ${s.nombre} eliminada`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const limpiarRechazadas = async () => {
    try {
      const { data } = await api.delete("/solicitudes-registro/rechazadas", { params: { rol } });
      toast.success(`${data.eliminadas} solicitud(es) rechazada(s) eliminada(s)`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copiar = () => {
    navigator.clipboard?.writeText(enlace);
    toast.success("Enlace copiado");
  };

  if (!solicitudes) return <Loading />;

  const pendientes = solicitudes.filter((s) => s.estado === "pendiente");
  const rechazadas = solicitudes.filter((s) => s.estado === "rechazada");

  return (
    <div data-testid={`panel-registro-${rol}`}>
      <div className="grid gap-8 lg:grid-cols-12">
        <section className="lg:col-span-5 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <div className="flex items-center gap-2 text-primary">
            <QrCode className="h-4 w-4" />
            <h2 className="font-display text-lg font-bold">{t.tituloQr}</h2>
          </div>
          <div className="mt-6 grid place-items-center rounded-lg border border-border bg-white p-6">
            {qr ? (
              <img src={qr} alt={t.tituloQr} className="h-56 w-56" data-testid={`qr-imagen-${rol}`} />
            ) : (
              <div className="h-56 w-56 animate-pulse rounded bg-muted" />
            )}
          </div>
          <p className="mt-4 break-all font-mono text-xs text-muted-foreground"
             data-testid={`qr-enlace-${rol}`}>{enlace}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" onClick={copiar}
                    data-testid={`copiar-enlace-${rol}`}>
              <Copy className="mr-2 h-4 w-4" /> Copiar enlace
            </Button>
            {qr && (
              <Button variant="outline" className="rounded-full" asChild
                      data-testid={`descargar-qr-${rol}`}>
                <a href={qr} download={t.archivo}>
                  <Download className="mr-2 h-4 w-4" /> Descargar QR
                </a>
              </Button>
            )}
            {qr && (
              <Button variant="outline" className="rounded-full" onClick={imprimirHoja}
                      data-testid={`imprimir-hoja-qr-${rol}`}>
                <Printer className="mr-2 h-4 w-4" /> Imprimir hoja
              </Button>
            )}
          </div>
        </section>

        <section className="lg:col-span-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold">
              {rol === "docente" ? "Solicitudes de docentes" : "Solicitudes de padres"}
            </h2>
            <div className="flex items-center gap-2">
              {rechazadas.length > 0 && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={limpiarRechazadas}
                        data-testid={`limpiar-rechazadas-${rol}`}>
                  <Eraser className="mr-1 h-3 w-3" /> Eliminar {rechazadas.length} rechazada(s)
                </Button>
              )}
              <Badge variant={pendientes.length ? "default" : "outline"}
                     data-testid={`contador-pendientes-${rol}`}>
                {pendientes.length} pendiente(s)
              </Badge>
            </div>
          </div>

          <div className="mt-4 space-y-4" data-testid={`lista-solicitudes-${rol}`}>
            {solicitudes.map((s) => (
              <article key={s.id} className="lift rounded-lg border border-border/60 bg-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <UserPlus className="h-4 w-4 text-primary" />
                      <h3 className="font-display text-base font-semibold">{s.nombre}</h3>
                      <Badge variant={s.estado === "pendiente" ? "outline" : s.estado === "aprobada" ? "secondary" : "destructive"}>
                        {s.estado}
                      </Badge>
                      <Badge variant="outline" className="uppercase">{s.rol}</Badge>
                    </div>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {s.email} · {s.telefono || "sin teléfono"}
                    </p>
                    {s.nombre_estudiante && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Estudiante: {s.nombre_estudiante} {s.grado_estudiante ? `· ${s.grado_estudiante}` : ""}
                      </p>
                    )}
                    {s.titulo && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Título: {s.titulo}{s.cedula ? ` · Cédula ${s.cedula}` : ""}
                      </p>
                    )}
                    {s.materias && (
                      <p className="mt-1 text-sm text-muted-foreground">Imparte: {s.materias}</p>
                    )}
                    {s.mensaje && <p className="mt-2 text-sm leading-relaxed">{s.mensaje}</p>}
                    <p className="mt-2 text-xs text-muted-foreground/70">Solicitado el {fecha(s.creado_en)}</p>
                  </div>
                  {s.estado === "pendiente" ? (
                    <div className="flex gap-2">
                      <Button size="sm" className="rounded-full" onClick={() => decidir(s, "aprobar")}
                              data-testid={`aprobar-${s.email}`}>
                        <Check className="mr-1 h-3 w-3" /> Aprobar
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full"
                              onClick={() => decidir(s, "rechazar")} data-testid={`rechazar-${s.email}`}>
                        <X className="mr-1 h-3 w-3" /> Rechazar
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => eliminar(s)}
                            title="Eliminar del historial" data-testid={`eliminar-solicitud-${s.email}`}>
                      <Trash2 className="mr-1 h-3 w-3 text-destructive" /> Eliminar
                    </Button>
                  )}
                </div>
              </article>
            ))}
            {solicitudes.length === 0 && (
              <Empty testId={`solicitudes-vacias-${rol}`}>
                Todavía no hay solicitudes {rol === "docente" ? "de docentes" : "de padres"}.
              </Empty>
            )}
          </div>
        </section>
      </div>

      <div className="hoja-imprimible" data-testid="hoja-qr-imprimible">
        <div style={{ textAlign: "center", fontFamily: "Arial, Helvetica, sans-serif" }}>
          <img src={config.tiene_logo
            ? `${API}/configuracion/imagen/logo?v=${config.imagen_version}` : LOGO}
               alt={config.nombre} style={{ height: "90px", margin: "0 auto" }} />
          <h1 style={{ fontSize: "26px", fontWeight: 800, marginTop: "16px" }}>{config.nombre}</h1>
          <p style={{ fontSize: "13px", color: "#555", marginTop: "4px" }}>
            {t.portal} · Año lectivo {config.anio_lectivo}
          </p>
          <h2 style={{ fontSize: "20px", fontWeight: 700, marginTop: "26px" }}>{t.tituloHoja}</h2>
          {qr && <img src={qr} alt="Código QR de registro"
                      style={{ width: "300px", height: "300px", margin: "18px auto" }} />}
          <p style={{ fontSize: "12px", fontFamily: "monospace", wordBreak: "break-all" }}>{enlace}</p>
          <ol style={{ textAlign: "left", maxWidth: "460px", margin: "24px auto 0",
                       fontSize: "14px", lineHeight: "24px" }}>
            {t.pasos.map((p) => <li key={p} style={{ marginBottom: "6px" }}>{p}</li>)}
          </ol>
          <p style={{ fontSize: "12px", color: "#555", marginTop: "26px" }}>
            ¿Necesita ayuda? Escriba a {config.email || "administracion@chikigenios.edu.do"}
          </p>
        </div>
      </div>
    </div>
  );
};
